import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../api';
import { useChat } from '../contexts/ChatContext';
import ProfilePictureTermsModal from '../components/ProfilePictureTermsModal';

interface User {
  id: number;
  username: string;
  display_name: string | null;
  email: string | null;
  photo_url?: string | null;
  qualifications?: string[];
  groups?: string[];
}

interface Message {
  id: number;
  room_id: number;
  sender_id: number;
  sender_name: string;
  sender_username: string;
  message: string;
  message_type?: 'text' | 'voice' | 'poll';
  metadata?: any;
  created_at: string;
  is_read: boolean;
}

interface Room {
  room_id: number;
  type: 'direct' | 'group' | 'broadcast';
  room_name: string | null;
  created_at: string;
  creator_id?: number | null;
  is_broadcast_released?: boolean;
  is_approved_writer?: boolean;
  is_archived?: boolean;
  is_pinned?: boolean;
  member_filter_qualifications?: string[];
  member_filter_groups?: string[];
  auto_sync_members?: boolean;
  other_member: {
    id: number;
    username: string;
    display_name: string | null;
    email: string | null;
    photo_url?: string | null;
  } | null;
  last_message: {
    id: number;
    message: string;
    message_type?: string;
    metadata?: any;
    sender_id: number;
    created_at: string;
  } | null;
  unread_count: number;
}

export default function Chat() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const [modalSearch, setModalSearch] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Group Creation
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedGroupUsers, setSelectedGroupUsers] = useState<number[]>([]);
  // Admin group filters (qual/groups - same as broadcast)
  const [groupQualifications, setGroupQualifications] = useState<string[]>([]);
  const [groupGroups, setGroupGroups] = useState<string[]>([]);
  const [groupAutoSync, setGroupAutoSync] = useState(false);
  const [showGroupFilterSection, setShowGroupFilterSection] = useState(false);

  // Broadcast Creation
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastName, setBroadcastName] = useState('');
  const [selectedQualifications, setSelectedQualifications] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [autoSyncMembers, setAutoSyncMembers] = useState(false);

  // Edit Broadcast Filters
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [filterRoomId, setFilterRoomId] = useState<number | null>(null);
  const [filterQualifications, setFilterQualifications] = useState<string[]>([]);
  const [filterGroups, setFilterGroups] = useState<string[]>([]);
  const [filterAutoSync, setFilterAutoSync] = useState(false);

  // Dynamic values
  const [allQualifications, setAllQualifications] = useState<any[]>([]);
  const [allGroups, setAllGroups] = useState<any[]>([]);

  // Broadcast member preview (computed)
  const selectedQualNames = selectedQualifications
    .map(id => allQualifications.find((q: any) => String(q.id) === String(id))?.name)
    .filter(Boolean) as string[];
  const selectedGroupNames = selectedGroups
    .map(id => allGroups.find((g: any) => String(g.id) === String(id))?.name)
    .filter(Boolean) as string[];

  const previewBroadcastMembers = users.filter(u => {
    const hasQual = selectedQualNames.length > 0 && (u.qualifications || []).some((q: string) => selectedQualNames.includes(q));
    const hasGroup = selectedGroupNames.length > 0 && (u.groups || []).some((g: string) => selectedGroupNames.includes(g));
    return hasQual || hasGroup;
  });

  // Admin group filter preview (computed)
  const groupQualNames = groupQualifications
    .map(id => allQualifications.find((q: any) => String(q.id) === String(id))?.name)
    .filter(Boolean) as string[];
  const groupGroupNames = groupGroups
    .map(id => allGroups.find((g: any) => String(g.id) === String(id))?.name)
    .filter(Boolean) as string[];

  const previewGroupFilterMembers = users.filter(u => {
    const hasQual = groupQualNames.length > 0 && (u.qualifications || []).some((q: string) => groupQualNames.includes(q));
    const hasGroup = groupGroupNames.length > 0 && (u.groups || []).some((g: string) => groupGroupNames.includes(g));
    return hasQual || hasGroup;
  });

  const filterQualNames = filterQualifications
    .map(id => allQualifications.find((q: any) => String(q.id) === String(id))?.name)
    .filter(Boolean) as string[];
  const filterGroupNames = filterGroups
    .map(id => allGroups.find((g: any) => String(g.id) === String(id))?.name)
    .filter(Boolean) as string[];

  const previewFilterMembers = users.filter(u => {
    const hasQual = filterQualNames.length > 0 && (u.qualifications || []).some((q: string) => filterQualNames.includes(q));
    const hasGroup = filterGroupNames.length > 0 && (u.groups || []).some((g: string) => filterGroupNames.includes(g));
    return hasQual || hasGroup;
  });

  // Release Broadcast
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releaseIsActive, setReleaseIsActive] = useState(false);
  const [approvedWriters, setApprovedWriters] = useState<number[]>([]);

  // Invite User
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteTargetUserId, setInviteTargetUserId] = useState<number | ''>('');

  // Delete Room
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteRoomId, setDeleteRoomId] = useState<number | null>(null);
  const [deleteRoomName, setDeleteRoomName] = useState('');
  const [deleteError, setDeleteError] = useState('');

  // Poll Creation
  const [showPollModal, setShowPollModal] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
  const [pollExpiresAt, setPollExpiresAt] = useState('');
  const [pollAllowMultiple, setPollAllowMultiple] = useState(false);

  // Voice Recording
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recordingIntervalRef = useRef<any>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const { socket, onlineUsers, activeRoomId, setActiveRoomId, refreshUnreadCount } = useChat();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Archive & Pin
  const [archivedRooms, setArchivedRooms] = useState<Room[]>([]);
  const [showArchive, setShowArchive] = useState(false);

  // Typing indicator
  const [typingUsers, setTypingUsers] = useState<Record<number, { displayName: string; roomId: number }>>({});
  const typingTimerRef = useRef<Record<number, any>>({});
  const currentTypingRef = useRef(false);
  const typingStopTimerRef = useRef<any>(null);

  // Read receipts
  const [readReceipts, setReadReceipts] = useState<Record<number, any[]>>({});

  const handleAvatarClick = () => {
    setShowTermsModal(true);
  };

  const handleTermsConfirm = () => {
    setShowTermsModal(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Das Bild darf maximal 5MB groß sein.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      try {
        await apiFetch('/profile/avatar', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ photo: base64 }),
        });
        setCurrentUserProfile(prev => prev ? { ...prev, photo_url: base64 } : null);
      } catch (err: any) {
        alert(err.message || 'Fehler beim Hochladen.');
      }
    };
    reader.readAsDataURL(file);
  };

  // Sync activeRoomId cleanup
  useEffect(() => {
    return () => {
      setActiveRoomId(null);
    };
  }, [setActiveRoomId]);

  // Load current user from JWT token
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser({
          id: payload.id || payload.sub,
          username: payload.username,
        });
      }
    } catch (e) {
      console.error('Failed to load user info from token:', e);
    }
  }, []);

  // Fetch initial rooms and users
  const loadRooms = async () => {
    try {
      const data = await apiFetch('/chat/rooms');
      setRooms(data);
    } catch (e) {
      console.error('Failed to load rooms:', e);
    }
  };

  const loadArchivedRooms = async () => {
    try {
      const data = await apiFetch('/chat/rooms/archived');
      setArchivedRooms(data);
    } catch (e) {
      console.error('Failed to load archived rooms:', e);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await apiFetch('/chat/users');
      setUsers(data);
    } catch (e) {
      console.error('Failed to load users:', e);
    }
  };

  const loadQualifications = async () => {
    try {
      const data = await apiFetch('/chat/qualifications');
      setAllQualifications(data);
    } catch (e) {
      console.error('Failed to load qualifications:', e);
    }
  };

  const loadGroups = async () => {
    try {
      const data = await apiFetch('/chat/groups');
      setAllGroups(data);
    } catch (e) {
      console.error('Failed to load groups:', e);
    }
  };

  useEffect(() => {
    loadRooms();
    loadArchivedRooms();
    loadUsers();
    loadQualifications();
    loadGroups();

    const loadProfile = async () => {
      try {
        const data = await apiFetch('/profile');
        setCurrentUserProfile(data);
      } catch (e) {
        console.error('Failed to load profile in Chat:', e);
      }
    };
    loadProfile();
  }, []);

  // Socket.io Shared Connection Event Registration
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (msg: Message) => {
      // If message is for currently active room, append it (deduplicated by msg.id)
      if (activeRoomId && msg.room_id === activeRoomId) {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) {
            return prev;
          }
          return [...prev, msg];
        });
        socket.emit('mark_read', { roomId: activeRoomId });
      } else {
        setRooms(prev => prev.map(r => {
          if (r.room_id === msg.room_id) {
            return {
              ...r,
              unread_count: r.unread_count + 1,
              last_message: { id: msg.id, message: msg.message, sender_id: msg.sender_id, created_at: msg.created_at }
            };
          }
          return r;
        }));
        // Auto-unarchive if new message arrived
        loadArchivedRooms();
      }
      loadRooms();
      refreshUnreadCount();
      // Clear typing indicator for sender
      setTypingUsers(prev => { const n = { ...prev }; delete n[msg.sender_id]; return n; });
    };

    const handleMessagesRead = ({ roomId, userId }: { roomId: number; userId: number }) => {
      if (activeRoomId && roomId === activeRoomId) {
        setMessages(prev => prev.map(m => m.sender_id === userId ? m : { ...m, is_read: true }));
      }
    };

    const handlePollVoted = ({ messageId, metadata }: { messageId: number; metadata: any }) => {
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, metadata } : m));
    };

    const handleUserTyping = ({ userId, displayName, roomId }: { userId: number; displayName: string; roomId: number }) => {
      if (roomId !== activeRoomId) return;
      setTypingUsers(prev => ({ ...prev, [userId]: { displayName, roomId } }));
      // Auto-clear after 4s if no stop event
      if (typingTimerRef.current[userId]) clearTimeout(typingTimerRef.current[userId]);
      typingTimerRef.current[userId] = setTimeout(() => {
        setTypingUsers(prev => { const n = { ...prev }; delete n[userId]; return n; });
      }, 4000);
    };

    const handleUserStoppedTyping = ({ userId }: { userId: number }) => {
      if (typingTimerRef.current[userId]) clearTimeout(typingTimerRef.current[userId]);
      setTypingUsers(prev => { const n = { ...prev }; delete n[userId]; return n; });
    };

    socket.on('new_message', handleNewMessage);
    socket.on('messages_read', handleMessagesRead);
    socket.on('poll_voted', handlePollVoted);
    socket.on('user_typing', handleUserTyping);
    socket.on('user_stopped_typing', handleUserStoppedTyping);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('messages_read', handleMessagesRead);
      socket.off('poll_voted', handlePollVoted);
      socket.off('user_typing', handleUserTyping);
      socket.off('user_stopped_typing', handleUserStoppedTyping);
    };
  }, [socket, activeRoomId, refreshUnreadCount]);

  // Load message history on active room change
  useEffect(() => {
    if (!activeRoomId) return;

    const fetchMessages = async () => {
      try {
        const data = await apiFetch(`/chat/rooms/${activeRoomId}/messages`);
        setMessages(data);
        
        // Mark room as read locally
        setRooms(prev => prev.map(r => r.room_id === activeRoomId ? { ...r, unread_count: 0 } : r));

        // Mark room as read on server via WebSocket
        if (socket) {
          socket.emit('mark_read', { roomId: activeRoomId });
          socket.emit('join_room', { roomId: activeRoomId });
        }
        refreshUnreadCount();
        setTypingUsers({});
        setReadReceipts({});

        // Load read receipts for group rooms
        const activeRoom = rooms.find(r => r.room_id === activeRoomId);
        if (activeRoom && (activeRoom.type === 'group' || activeRoom.type === 'broadcast')) {
          try {
            const msgIds = data.map((m: any) => m.id);
            if (msgIds.length > 0) {
              const receipts = await apiFetch(`/chat/rooms/${activeRoomId}/read-receipts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messageIds: msgIds }),
              });
              setReadReceipts(receipts);
            }
          } catch (_) {}
        }
      } catch (e) {
        console.error('Failed to fetch messages:', e);
      }
    };

    fetchMessages();
  }, [activeRoomId, socket, refreshUnreadCount]);

  // Auto-scroll messages to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Voice recording handlers
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const file = new File([audioBlob], 'voice-recording.webm', { type: 'audio/webm' });
        
        const formData = new FormData();
        formData.append('file', file);

        try {
          const uploadRes = await apiFetch('/chat/voice/upload', {
            method: 'POST',
            body: formData,
          });

          if (socket && activeRoomId && uploadRes.url) {
            socket.emit('send_message', {
              roomId: activeRoomId,
              message: '[Sprachnachricht]',
              messageType: 'voice',
              metadata: { url: uploadRes.url }
            });
          }
        } catch (err) {
          console.error('Failed to upload voice message:', err);
          alert('Sprachnachricht konnte nicht gesendet werden.');
        }

        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingSeconds(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Failed to start voice recording:', err);
      alert('Mikrofonzugriff verweigert oder nicht verfügbar.');
    }
  };

  const stopRecording = (shouldSend = true) => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }

    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      if (!shouldSend) {
        mediaRecorder.onstop = () => {};
      }
      mediaRecorder.stop();
    }

    setIsRecording(false);
    setMediaRecorder(null);
  };

  // Group & Broadcast handlers
  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    const isAdminFiltered = currentUserProfile?.is_admin && showGroupFilterSection && (groupQualifications.length > 0 || groupGroups.length > 0);
    // If admin used filters, don't require manual user selection
    if (!groupName.trim()) return;
    if (!isAdminFiltered && selectedGroupUsers.length === 0) return;

    try {
      const body: any = {
        name: groupName.trim(),
        userIds: isAdminFiltered ? previewGroupFilterMembers.map(u => u.id) : selectedGroupUsers,
      };
      if (isAdminFiltered) {
        body.memberFilterQualifications = groupQualifications;
        body.memberFilterGroups = groupGroups;
        body.autoSyncMembers = groupAutoSync;
      }
      const res = await apiFetch('/chat/rooms/group', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      await loadRooms();
      setActiveRoomId(res.roomId);
      setShowGroupModal(false);
      setGroupName('');
      setSelectedGroupUsers([]);
      setGroupQualifications([]);
      setGroupGroups([]);
      setGroupAutoSync(false);
      setShowGroupFilterSection(false);
    } catch (err) {
      console.error('Failed to create group:', err);
    }
  };

  const handleCreateBroadcast = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!broadcastName.trim()) return;

    try {
      const res = await apiFetch('/chat/rooms/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: broadcastName.trim(),
          memberFilterQualifications: selectedQualifications,
          memberFilterGroups: selectedGroups,
          autoSyncMembers: autoSyncMembers
        }),
      });
      await loadRooms();
      setActiveRoomId(res.roomId);
      setShowBroadcastModal(false);
      setBroadcastName('');
      setSelectedQualifications([]);
      setSelectedGroups([]);
      setAutoSyncMembers(false);
    } catch (err) {
      console.error('Failed to create broadcast channel:', err);
    }
  };

  const handleUpdateFilters = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!filterRoomId) return;

    try {
      await apiFetch(`/chat/rooms/${filterRoomId}/filters`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          memberFilterQualifications: filterQualifications,
          memberFilterGroups: filterGroups,
          autoSyncMembers: filterAutoSync
        }),
      });
      await loadRooms();
      setShowFiltersModal(false);
      setFilterRoomId(null);
    } catch (err) {
      console.error('Failed to update room filters:', err);
    }
  };

  const handleReleaseBroadcast = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeRoomId) return;

    try {
      await apiFetch(`/chat/rooms/${activeRoomId}/release-broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isReleased: releaseIsActive, approvedUserIds: approvedWriters }),
      });
      await loadRooms();
      setShowReleaseModal(false);
    } catch (err) {
      console.error('Failed to release broadcast channel:', err);
    }
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeRoomId || !inviteTargetUserId) return;

    try {
      await apiFetch(`/chat/rooms/${activeRoomId}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId: Number(inviteTargetUserId) }),
      });
      await loadRooms();
      setShowInviteModal(false);
      setInviteTargetUserId('');
    } catch (err) {
      console.error('Failed to invite user:', err);
    }
  };

  // Poll handlers
  const handleCreatePoll = async (e: React.FormEvent) => {
    e.preventDefault();
    const filteredOptions = pollOptions.filter(o => o.trim() !== '');
    if (!pollQuestion.trim() || filteredOptions.length < 2 || !activeRoomId || !socket) return;

    socket.emit('send_message', {
      roomId: activeRoomId,
      message: pollQuestion.trim(),
      messageType: 'poll',
      metadata: {
        question: pollQuestion.trim(),
        options: filteredOptions.map(o => o.trim()),
        votes: {},
        allowMultiple: pollAllowMultiple,
        expiresAt: pollExpiresAt || null,
      }
    });

    setShowPollModal(false);
    setPollQuestion('');
    setPollOptions(['', '']);
    setPollExpiresAt('');
    setPollAllowMultiple(false);
  };

  const handleVotePoll = (messageId: number, optionIndex: number, allowMultiple: boolean, currentVotes: Record<string, any>) => {
    if (!socket) return;
    const userId = currentUser?.id?.toString();
    if (!userId) return;

    if (allowMultiple) {
      // Multi-select: votes stored as array per user
      const existingVotes: number[] = Array.isArray(currentVotes[userId]) ? currentVotes[userId] : [];
      const alreadyVoted = existingVotes.includes(optionIndex);
      const newVotes = alreadyVoted
        ? existingVotes.filter((v: number) => v !== optionIndex)  // deselect
        : [...existingVotes, optionIndex];  // add selection
      socket.emit('vote_poll', { messageId, optionIndex, allowMultiple: true, newVotes });
    } else {
      socket.emit('vote_poll', { messageId, optionIndex });
    }
  };

  // Send message
  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputText.trim() || !activeRoomId || !socket) return;

    // Stop typing indicator
    if (currentTypingRef.current) {
      socket.emit('typing_stop', { roomId: activeRoomId });
      currentTypingRef.current = false;
    }
    if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);

    socket.emit('send_message', {
      roomId: activeRoomId,
      message: inputText.trim(),
    });

    setInputText('');
  };

  // Handle input changes: emit typing_start event
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    if (!socket || !activeRoomId || !currentUserProfile) return;

    if (!currentTypingRef.current && e.target.value.trim()) {
      const displayName = currentUserProfile.vorname
        ? `${currentUserProfile.vorname} ${currentUserProfile.name || ''}`.trim()
        : currentUserProfile.display_name || currentUserProfile.username;
      socket.emit('typing_start', { roomId: activeRoomId, displayName });
      currentTypingRef.current = true;
    }

    // Auto-stop after 3s of no keystrokes
    if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
    typingStopTimerRef.current = setTimeout(() => {
      if (currentTypingRef.current && socket && activeRoomId) {
        socket.emit('typing_stop', { roomId: activeRoomId });
        currentTypingRef.current = false;
      }
    }, 3000);

    if (!e.target.value.trim() && currentTypingRef.current) {
      socket.emit('typing_stop', { roomId: activeRoomId });
      currentTypingRef.current = false;
    }
  };

  // Toggle archive for direct chats
  const handleToggleArchive = async (roomId: number) => {
    try {
      await apiFetch(`/chat/rooms/${roomId}/archive`, { method: 'POST' });
      await loadRooms();
      await loadArchivedRooms();
      if (activeRoomId === roomId) setActiveRoomId(null);
    } catch (err: any) {
      console.error('Archive toggle failed:', err.message);
    }
  };

  // Toggle pin for direct chats
  const handleTogglePin = async (roomId: number) => {
    try {
      await apiFetch(`/chat/rooms/${roomId}/pin`, { method: 'POST' });
      await loadRooms();
    } catch (err: any) {
      console.error('Pin toggle failed:', err.message);
    }
  };

  // Delete room
  const handleDeleteRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (deleteConfirmText !== 'LÖSCHEN') {
      setDeleteError('Bitte gib genau "LÖSCHEN" ein um fortzufahren.');
      return;
    }
    if (!deleteRoomId) return;
    try {
      await apiFetch(`/chat/rooms/${deleteRoomId}`, { method: 'DELETE' });
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      setDeleteRoomId(null);
      setDeleteRoomName('');
      setDeleteError('');
      setActiveRoomId(null);
      await loadRooms();
    } catch (err: any) {
      setDeleteError(err.message || 'Fehler beim Löschen der Gruppe.');
    }
  };

  // Start direct chat with user
  const handleStartDirectChat = async (targetUserId: number) => {
    try {
      const res = await apiFetch('/chat/rooms/direct', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId }),
      });
      
      await loadRooms();
      setActiveRoomId(res.roomId);
      setShowNewChatModal(false);
      setModalSearch('');
    } catch (e) {
      console.error('Failed to create direct chat:', e);
    }
  };

  // Filter rooms
  const filteredRooms = rooms.filter(room => {
    const name = (room.type === 'group' || room.type === 'broadcast') 
      ? room.room_name 
      : (room.other_member?.display_name || room.other_member?.username);
    return name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Filter users for new chat modal
  const filteredUsers = users.filter(user => {
    const name = user.display_name || user.username;
    const qualStr = (user.qualifications || []).join(' ');
    const groupStr = (user.groups || []).join(' ');
    const searchTarget = `${name} ${qualStr} ${groupStr}`.toLowerCase();
    return searchTarget.includes(modalSearch.toLowerCase());
  });

  const getRoomName = (room: Room) => {
    if (room.type === 'group' || room.type === 'broadcast') {
      return room.room_name || (room.type === 'group' ? 'Gruppen-Chat' : 'Broadcast-Kanal');
    }
    return room.other_member?.display_name || room.other_member?.username || 'Unbekannt';
  };

  const getActiveRoomDetails = () => {
    if (!activeRoomId) return null;
    const room = rooms.find(r => r.room_id === activeRoomId);
    if (!room) return null;
    return room;
  };

  const activeRoom = getActiveRoomDetails();

  return (
    <div className="flex h-[calc(100vh-120px)] bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-2xl">
      {/* Sidebar - Rooms List */}
      <div className="w-80 flex flex-col bg-slate-50 dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex flex-col gap-3">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/png, image/jpeg, image/jpg, image/webp"
            className="hidden"
          />
          <ProfilePictureTermsModal
            isOpen={showTermsModal}
            onClose={() => setShowTermsModal(false)}
            onConfirm={handleTermsConfirm}
          />

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div 
                onClick={handleAvatarClick}
                className="relative group w-8 h-8 rounded-full overflow-hidden border border-slate-350 dark:border-slate-700 shadow-sm cursor-pointer flex-shrink-0"
                title="Profilbild hochladen"
              >
                {currentUserProfile?.photo_url ? (
                  <img src={currentUserProfile.photo_url} alt="My Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-sm">
                    👤
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-[8px] font-bold transition-opacity">
                  📷
                </div>
              </div>
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                Portal Chat
              </h2>
            </div>
            <button
              onClick={() => setShowNewChatModal(true)}
              className="p-1.5 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white rounded-lg transition-colors text-sm font-semibold flex items-center gap-1 shadow-md shadow-blue-900/30"
            >
              ➕ Neu
            </button>
          </div>
          <input
            type="text"
            placeholder="Chats durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
          />
        </div>

        {/* Room items list */}
        <div className="flex-grow overflow-y-auto">
          {filteredRooms.length === 0 ? (
            <div className="p-6 text-center text-slate-400 dark:text-slate-500 text-sm">
              Keine Chats gefunden. Klicke auf "Neu" um einen Chat zu starten.
            </div>
          ) : (
            filteredRooms.map(room => {
              const isActive = activeRoomId === room.room_id;
              const hasUnread = room.unread_count > 0;
              const lastMsg = room.last_message;
              const lastMsgText = !lastMsg 
                ? 'Keine Nachrichten' 
                : lastMsg.message_type === 'voice' 
                  ? '🎤 Sprachnachricht'
                  : lastMsg.message_type === 'poll'
                    ? `📊 ${lastMsg.metadata?.question || 'Umfrage'}`
                    : lastMsg.message || 'Keine Nachrichten';
              const isOnline = room.type === 'direct' && room.other_member && onlineUsers.has(room.other_member.id);
              
              return (
                <div
                  key={room.room_id}
                  onClick={() => setActiveRoomId(room.room_id)}
                  className={`flex items-center gap-3 p-4 border-b border-slate-100 dark:border-slate-900/50 cursor-pointer transition-all ${
                    isActive 
                      ? 'bg-blue-50 dark:bg-slate-800 text-blue-950 dark:text-white' 
                      : 'hover:bg-slate-100 dark:hover:bg-slate-900/60 text-slate-600 dark:text-slate-300'
                  }`}
                >
                  {/* Room Icon / Avatar */}
                  <div className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center overflow-hidden border text-xl ${
                    room.type === 'broadcast'
                      ? 'bg-blue-100 dark:bg-blue-950/40 border-blue-300 dark:border-blue-800'
                      : room.type === 'group'
                        ? 'bg-emerald-100 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800'
                        : isOnline 
                          ? 'bg-slate-200 dark:bg-slate-800 border-2 border-green-500 dark:border-green-400 shadow-[0_0_8px_rgba(34,197,94,0.5)]'
                          : 'bg-slate-200 dark:bg-slate-800 border-slate-300 dark:border-slate-700'
                  }`}>
                    {room.type === 'direct' && room.other_member?.photo_url ? (
                      <img src={room.other_member.photo_url} alt="Avatar" className="w-full h-full object-cover" />
                    ) : room.type === 'broadcast' ? (
                      '📢'
                    ) : room.type === 'group' ? (
                      '👥'
                    ) : (
                      '👤'
                    )}
                  </div>

                  {/* Room Info */}
                  <div className="flex-grow min-w-0">
                    <div className="flex justify-between items-baseline mb-1">
                      <span className={`font-semibold text-sm truncate pr-1 ${isActive ? 'text-blue-950 dark:text-slate-100' : 'text-slate-800 dark:text-slate-250'}`}>
                        {getRoomName(room)}
                      </span>
                      {room.last_message && (
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0">
                          {new Date(room.last_message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs truncate ${hasUnread ? 'text-blue-500 dark:text-blue-400 font-medium' : 'text-slate-400 dark:text-slate-500'}`}>
                      {lastMsgText}
                    </p>
                    {/* Type badge for group/broadcast */}
                    {(room.type === 'group' || room.type === 'broadcast') && (
                      <span className={`text-[9px] font-bold uppercase tracking-wide ${
                        room.type === 'broadcast' ? 'text-blue-500 dark:text-blue-400' : 'text-emerald-600 dark:text-emerald-400'
                      }`}>
                        {room.type === 'broadcast' ? 'Broadcast-Kanal' : 'Gruppe'}
                      </span>
                    )}
                  </div>

                  {/* Pin indicator + Unread Badge */}
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {room.is_pinned && (
                      <span className="text-amber-500 dark:text-amber-400" title="Angepinnt">📌</span>
                    )}
                    {hasUnread && (
                      <div className="min-w-5 h-5 px-1.5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                        {room.unread_count}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {/* Archived Chats Section */}
          {archivedRooms.length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-800">
              <button
                onClick={() => setShowArchive(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-900/50 transition-colors font-semibold uppercase tracking-wide"
              >
                <span className="flex items-center gap-2">
                  🗃️ Archiv ({archivedRooms.length})
                </span>
                <span>{showArchive ? '▲' : '▼'}</span>
              </button>
              {showArchive && (
                <div className="bg-slate-50/50 dark:bg-slate-900/30">
                  {archivedRooms.map(room => (
                    <div key={room.room_id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800/50">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center overflow-hidden border border-slate-300 dark:border-slate-700">
                        {room.other_member?.photo_url ? (
                          <img src={room.other_member.photo_url} alt="" className="w-full h-full object-cover opacity-60" />
                        ) : (
                          <span className="text-base opacity-60">👤</span>
                        )}
                      </div>
                      <div className="flex-grow min-w-0">
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 truncate">
                          {room.other_member?.display_name || room.other_member?.username || 'Unbekannt'}
                        </p>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500">Archiviert</p>
                      </div>
                      <button
                        onClick={() => handleToggleArchive(room.room_id)}
                        className="flex-shrink-0 text-[10px] px-2 py-1 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-lg transition-colors font-semibold"
                      >
                        Zurück
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Panel */}
      <div className="flex-grow flex flex-col bg-white dark:bg-slate-900">
        {activeRoom ? (
          <>
            {/* Active Room Header */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-950">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center overflow-hidden text-2xl border border-slate-300 dark:border-slate-700">
                  {activeRoom.type === 'direct' && activeRoom.other_member?.photo_url ? (
                    <img src={activeRoom.other_member.photo_url} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    activeRoom.type === 'group' ? '👥' : activeRoom.type === 'broadcast' ? '📢' : '👤'
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 dark:text-slate-100">{getRoomName(activeRoom)}</h3>
                  {activeRoom.type === 'direct' && activeRoom.other_member && (
                    <p className="text-xs font-medium flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${onlineUsers.has(activeRoom.other_member.id) ? 'bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.8)] animate-pulse' : 'bg-slate-400 dark:bg-slate-600'}`} />
                      <span className={onlineUsers.has(activeRoom.other_member.id) ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'}>
                        {onlineUsers.has(activeRoom.other_member.id) ? 'Aktiv gerade' : 'Offline'}
                      </span>
                    </p>
                  )}
                  {activeRoom.type === 'broadcast' && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                      Broadcast-Kanal {activeRoom.is_broadcast_released ? '(Freigegeben)' : '(Nur Admins)'}
                    </p>
                  )}
                  {activeRoom.type === 'group' && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      Gruppe {activeRoom.creator_id === currentUser?.id ? '(Von Dir erstellt)' : ''}
                    </p>
                  )}
                </div>
              </div>
              {/* Header Actions */}
              <div className="flex gap-2">
                {/* Pin & Archive for direct chats */}
                {activeRoom.type === 'direct' && (
                  <>
                    <button
                      onClick={() => handleTogglePin(activeRoom.room_id)}
                      title={activeRoom.is_pinned ? 'Anpinnen aufheben' : 'Chat anpinnen'}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 border ${
                        activeRoom.is_pinned
                          ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900/30'
                          : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700'
                      }`}
                    >
                      📌 {activeRoom.is_pinned ? 'Angepinnt' : 'Anpinnen'}
                    </button>
                    <button
                      onClick={() => handleToggleArchive(activeRoom.room_id)}
                      title="Chat archivieren"
                      className="px-3 py-1.5 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 border border-slate-300 dark:border-slate-700"
                    >
                      🗃️ Archivieren
                    </button>
                  </>
                )}
                {activeRoom.type === 'group' && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="px-3 py-1.5 bg-slate-200 dark:bg-slate-850 hover:bg-slate-300 dark:hover:bg-slate-750 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1"
                  >
                    👤➕ Einladen
                  </button>
                )}
                {activeRoom.type === 'broadcast' && (currentUserProfile?.is_admin || activeRoom.creator_id === currentUser?.id) && (
                  <>
                    <button
                      onClick={() => {
                        setFilterRoomId(activeRoom.room_id);
                        setFilterQualifications(activeRoom.member_filter_qualifications || []);
                        setFilterGroups(activeRoom.member_filter_groups || []);
                        setFilterAutoSync(activeRoom.auto_sync_members || false);
                        setShowFiltersModal(true);
                      }}
                      className="px-3 py-1.5 bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 border border-purple-200 dark:border-purple-900/30"
                    >
                      🔍 Filter einrichten
                    </button>
                    <button
                      onClick={() => {
                        setReleaseIsActive(activeRoom.is_broadcast_released || false);
                        setShowReleaseModal(true);
                      }}
                      className="px-3 py-1.5 bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 border border-blue-200 dark:border-blue-900/30"
                    >
                      ⚙️ Kanal freigeben
                    </button>
                  </>
                )}
                {/* 🗑️ Löschen-Button (für Gruppen und Broadcasts, mit Berechtigungsprüfung) */}
                {(activeRoom.type === 'group' || activeRoom.type === 'broadcast') && (
                  currentUserProfile?.is_admin || activeRoom.creator_id === currentUser?.id
                ) && (
                  <button
                    onClick={() => {
                      setDeleteRoomId(activeRoom.room_id);
                      setDeleteRoomName(activeRoom.room_name || (activeRoom.type === 'broadcast' ? 'Broadcast-Kanal' : 'Gruppe'));
                      setDeleteConfirmText('');
                      setDeleteError('');
                      setShowDeleteModal(true);
                    }}
                    className="px-3 py-1.5 bg-red-100 dark:bg-red-950/40 hover:bg-red-200 dark:hover:bg-red-900/60 text-red-700 dark:text-red-400 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 border border-red-200 dark:border-red-900/30"
                    title="Gruppe löschen"
                  >
                    🗑️ Löschen
                  </button>
                )}
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div className="flex-grow overflow-y-auto p-4 space-y-4 bg-white dark:bg-slate-950">
              {messages.map((msg, index) => {
                const isMe = currentUser && String(msg.sender_id) === String(currentUser.id);
                
                return (
                  <div key={msg.id || index} className={`w-full flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                    {/* Sender display name */}
                    {!isMe && (
                      <span className="text-[11px] text-slate-500 dark:text-slate-400 mb-0.5 ml-2">
                        {msg.sender_name}
                      </span>
                    )}
                    
                    {/* Bubble body – poll messages get no outer bubble padding since they render their own card */}
                    <div className={`max-w-[75%] shadow-md ${
                      msg.message_type === 'poll'
                        ? isMe
                          ? 'rounded-2xl rounded-tr-none'
                          : 'rounded-2xl rounded-tl-none'
                        : isMe
                          ? 'bg-blue-600 text-white rounded-2xl rounded-tr-none px-4 py-2.5'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-2xl rounded-tl-none px-4 py-2.5 border border-slate-200 dark:border-slate-700'
                    }`}>
                      {msg.message_type === 'voice' ? (
                        <audio src={msg.metadata?.url} controls className="max-w-full my-1 rounded-lg" />
                      ) : msg.message_type === 'poll' ? (() => {
                          const votes = msg.metadata?.votes || {};
                          const allowMultiple = msg.metadata?.allowMultiple || false;
                          const expiresAt = msg.metadata?.expiresAt;
                          const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false;
                          const userId = currentUser?.id?.toString();

                          // Count votes per option (handles both single int and multi-select array)
                          const getOptionVotes = (idx: number) => {
                            return Object.values(votes).filter((v: any) =>
                              allowMultiple ? Array.isArray(v) && v.includes(idx) : v === idx
                            ).length;
                          };
                          const totalVoters = Object.keys(votes).length;

                          // Check if user voted for this option
                          const userVotedFor = (idx: number) => {
                            if (!userId || votes[userId] === undefined) return false;
                            if (allowMultiple) return Array.isArray(votes[userId]) && votes[userId].includes(idx);
                            return votes[userId] === idx;
                          };

                          // Time remaining
                          const timeLeft = expiresAt ? (() => {
                            const diff = new Date(expiresAt).getTime() - Date.now();
                            if (diff <= 0) return null;
                            const h = Math.floor(diff / 3600000);
                            const m = Math.floor((diff % 3600000) / 60000);
                            if (h > 24) return `${Math.floor(h/24)}T ${h%24}h`;
                            if (h > 0) return `${h}h ${m}m`;
                            return `${m}m`;
                          })() : null;

                          return (
                            <div className={`my-0 p-3 rounded-2xl min-w-[240px] max-w-full ${
                              isMe
                                ? 'bg-blue-700/80 dark:bg-blue-900/70 border border-blue-500/50 text-white'
                                : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100'
                            }`}>
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <h4 className="font-bold text-sm">
                                  {isExpired ? '🔒' : '📊'} {msg.metadata?.question}
                                </h4>
                                {allowMultiple && (
                                  <span className={`flex-shrink-0 text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase border ${
                                    isMe
                                      ? 'bg-white/20 text-white border-white/30'
                                      : 'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800'
                                  }`}>
                                    Mehrfach
                                  </span>
                                )}
                              </div>

                              {/* Expiry banner */}
                              {expiresAt && (
                                <div className={`flex items-center gap-1 text-[10px] font-semibold rounded-lg px-2 py-1 mb-2 ${
                                  isExpired
                                    ? isMe ? 'bg-red-900/40 text-red-200' : 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400'
                                    : isMe ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400'
                                }`}>
                                  {isExpired ? '🔒 Abstimmung beendet' : `⏱ Noch ${timeLeft}`}
                                </div>
                              )}

                              <div className="space-y-2">
                                {msg.metadata?.options?.map((opt: string, idx: number) => {
                                  const optVotes = getOptionVotes(idx);
                                  const totalForPct = Math.max(totalVoters, 1);
                                  const pct = totalVoters > 0 ? Math.round((optVotes / totalForPct) * 100) : 0;
                                  const voted = userVotedFor(idx);
                                  const disabled = isExpired;

                                  return (
                                    <div key={idx} className="relative overflow-hidden rounded-lg">
                                      <button
                                        type="button"
                                        onClick={() => !disabled && handleVotePoll(msg.id, idx, allowMultiple, votes)}
                                        disabled={disabled}
                                        className={`w-full text-left text-xs px-3 py-2 rounded-lg border transition-all relative z-10 flex justify-between items-center ${
                                          isMe
                                            ? disabled
                                              ? 'cursor-not-allowed opacity-60 border-white/20 text-white/60'
                                              : voted
                                                ? 'border-white/60 bg-white/20 text-white font-semibold cursor-pointer'
                                                : 'border-white/25 hover:bg-white/15 text-white/90 cursor-pointer'
                                            : disabled
                                              ? 'cursor-not-allowed opacity-70 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500'
                                              : voted
                                                ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold cursor-pointer'
                                                : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer'
                                        }`}
                                      >
                                        <span className="flex items-center gap-1.5">
                                          {allowMultiple && (
                                            <span className={`w-3.5 h-3.5 rounded flex-shrink-0 border-2 flex items-center justify-center ${
                                              voted
                                                ? 'bg-white/90 border-white/90'
                                                : isMe ? 'border-white/40' : 'border-slate-400 dark:border-slate-500'
                                            }`}>
                                              {voted && <span className={`text-[8px] font-bold ${isMe ? 'text-blue-700' : 'text-blue-600 dark:text-blue-400'}`}>✓</span>}
                                            </span>
                                          )}
                                          {!allowMultiple && voted && <span className={isMe ? 'text-white' : 'text-blue-500 dark:text-blue-400'}>●</span>}
                                          {opt}
                                        </span>
                                        <span className={`font-mono text-[10px] ${ isMe ? 'text-white/70' : 'text-slate-500 dark:text-slate-400' }`}>{pct}% ({optVotes})</span>
                                      </button>
                                      <div
                                        className={`absolute left-0 top-0 bottom-0 rounded-lg z-0 transition-all duration-300 ${
                                          isMe
                                            ? voted ? 'bg-white/25' : 'bg-white/10'
                                            : voted ? 'bg-blue-500/15 dark:bg-blue-400/15' : 'bg-slate-200/50 dark:bg-slate-600/20'
                                        }`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>

                              <p className={`text-[10px] mt-2 text-right ${ isMe ? 'text-white/60' : 'text-slate-400 dark:text-slate-500' }`}>
                                {totalVoters} {totalVoters === 1 ? 'Stimme' : 'Stimmen'}
                                {expiresAt && !isExpired && <> · bis {new Date(expiresAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</>}
                              </p>
                            </div>
                          );
                        })() : (
                        <p className="text-sm break-words leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                      )}
                      
                      {/* Meta: time + read checks – only shown for non-poll messages */}
                      {msg.message_type !== 'poll' && (
                      <div className="flex items-center justify-end gap-1 mt-1">
                        <span className={`text-[9px] ${isMe ? 'text-blue-200' : 'text-slate-400 dark:text-slate-500'}`}>
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isMe && (
                          <span className="text-[10px]" title={msg.is_read ? 'Gelesen' : 'Gesendet'}>
                            {msg.is_read ? '✔️✔️' : '✔️'}
                          </span>
                        )}
                      </div>
                      )}
                    </div>
                    {/* Read receipts for group/broadcast messages sent by me */}
                    {isMe && (activeRoom.type === 'group' || activeRoom.type === 'broadcast') && readReceipts[msg.id] && readReceipts[msg.id].length > 0 && (
                      <div className="flex items-center gap-1 mt-1 mr-1 justify-end">
                        <span className="text-[9px] text-slate-400 dark:text-slate-500">Gelesen von:</span>
                        {readReceipts[msg.id].slice(0, 5).map((r: any) => (
                          <span key={r.user_id} title={r.display_name} className="flex items-center">
                            {r.photo_url ? (
                              <img src={r.photo_url} alt={r.display_name} className="w-4 h-4 rounded-full object-cover border border-slate-300 dark:border-slate-600" />
                            ) : (
                              <span className="w-4 h-4 rounded-full bg-blue-200 dark:bg-blue-800 flex items-center justify-center text-[8px] text-blue-700 dark:text-blue-300 font-bold border border-slate-300 dark:border-slate-600">
                                {r.display_name?.charAt(0)}
                              </span>
                            )}
                          </span>
                        ))}
                        {readReceipts[msg.id].length > 5 && (
                          <span className="text-[9px] text-slate-400">+{readReceipts[msg.id].length - 5}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Typing indicator */}
            {Object.values(typingUsers).filter(t => t.roomId === activeRoomId).length > 0 && (
              <div className="px-4 pb-1 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 animate-pulse">
                <span className="flex gap-0.5">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
                <span>
                  {Object.values(typingUsers)
                    .filter(t => t.roomId === activeRoomId)
                    .map(t => t.displayName)
                    .join(', ')} schreibt...
                </span>
              </div>
            )}

            {/* Input Message Area */}
            {activeRoom.type === 'broadcast' && !activeRoom.is_approved_writer ? (
              <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-950 text-center text-xs text-slate-500 dark:text-slate-400 font-medium italic">
                📢 Dies ist ein Broadcast-Kanal. Nur Administratoren und freigegebene Schreiber können hier senden.
              </div>
            ) : (
              <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex items-center gap-2">
                {isRecording ? (
                  <div className="flex-grow flex items-center justify-between bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 rounded-xl px-4 py-2 text-sm text-red-700 dark:text-red-400 font-semibold animate-pulse">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-ping" />
                      <span>Aufnahme läuft... {recordingSeconds}s</span>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        type="button" 
                        onClick={() => stopRecording(false)} 
                        className="text-xs px-2.5 py-1.5 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
                      >
                        Abbrechen ✕
                      </button>
                      <button 
                        type="button" 
                        onClick={() => stopRecording(true)} 
                        className="text-xs px-2.5 py-1.5 bg-red-650 hover:bg-red-600 text-white rounded-lg transition-colors"
                      >
                        Senden 🚀
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={startRecording}
                      title="Sprachnachricht aufnehmen"
                      className="p-3 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl transition-all"
                    >
                      🎤
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPollModal(true)}
                      title="Umfrage erstellen"
                      className="p-3 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl transition-all"
                    >
                      📊
                    </button>
                    <input
                      type="text"
                      value={inputText}
                      onChange={handleInputChange}
                      placeholder="Schreibe eine Nachricht..."
                      className="flex-grow bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                    />
                    <button
                      type="submit"
                      disabled={!inputText.trim()}
                      className="px-5 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:hover:bg-blue-600 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-900/20 flex items-center justify-center"
                    >
                      Senden 🚀
                    </button>
                  </>
                )}
              </form>
            )}
          </>
        ) : (
          /* Empty Chat Area */
          <div className="flex-grow flex flex-col items-center justify-center p-8 text-center bg-slate-50/30 dark:bg-slate-900/10">
            <span className="text-6xl mb-4 animate-bounce">💬</span>
            <h3 className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">Portal Chat</h3>
            <p className="text-slate-400 dark:text-slate-500 text-sm max-w-sm">
              Wähle einen Chat in der Seitenleiste aus oder starte eine neue Konversation, um direkt loszulegen.
            </p>
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl animate-in fade-in duration-200">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">Neuer Chat</h3>
              <button
                onClick={() => {
                  setShowNewChatModal(false);
                  setModalSearch('');
                }}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Quick Actions for Groups/Broadcasts */}
            <div className="p-3 border-b border-slate-150 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowNewChatModal(false);
                  setShowGroupModal(true);
                }}
                className="flex-1 py-2 px-3 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-755 text-slate-800 dark:text-slate-200 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1"
              >
                👥 Neue Gruppe
              </button>
              {currentUserProfile?.is_admin && (
                <button
                  type="button"
                  onClick={() => {
                    setShowNewChatModal(false);
                    setShowBroadcastModal(true);
                  }}
                  className="flex-1 py-2 px-3 bg-blue-100 dark:bg-blue-955/40 hover:bg-blue-200 text-blue-700 dark:text-blue-400 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1 border border-blue-200 dark:border-blue-900/30"
                >
                  📢 Broadcast-Kanal
                </button>
              )}
            </div>

            {/* Modal Search + Badge Toggle */}
            <div className="p-3 border-b border-slate-100 dark:border-slate-900 bg-slate-50 dark:bg-slate-900/20 flex gap-2 items-center">
              <input
                type="text"
                placeholder="Suche nach Name oder Rolle (z.B. Admin)..."
                value={modalSearch}
                onChange={(e) => setModalSearch(e.target.value)}
                className="flex-grow bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-blue-500 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowBadges(prev => !prev)}
                title={showBadges ? 'Gruppen & Qualifikationen ausblenden' : 'Gruppen & Qualifikationen anzeigen'}
                className={`flex-shrink-0 px-2.5 py-2 rounded-lg text-xs font-semibold border transition-all ${
                  showBadges
                    ? 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900/30'
                    : 'bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                🏷️
              </button>
            </div>

            {/* Modal Users list */}
            <div className="flex-grow overflow-y-auto max-h-[300px]">
              {filteredUsers.length === 0 ? (
                <div className="p-8 text-center text-slate-400 dark:text-slate-500 text-sm">
                  Keine Benutzer gefunden
                </div>
              ) : (
                filteredUsers.map(user => {
                  const isOnline = onlineUsers.has(user.id);
                  return (
                    <div
                      key={user.id}
                      onClick={() => handleStartDirectChat(user.id)}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-900/60 cursor-pointer border-b border-slate-100 dark:border-slate-900/30 transition-colors"
                    >
                      <div className={`w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center overflow-hidden font-bold border ${
                        isOnline 
                          ? 'border-2 border-green-500 dark:border-green-400 shadow-[0_0_8px_rgba(34,197,94,0.5)]' 
                          : 'border-slate-300 dark:border-slate-700'
                      }`}>
                        {user.photo_url ? (
                          <img src={user.photo_url} alt="Avatar" className="w-full h-full object-cover" />
                        ) : (
                          '👤'
                        )}
                      </div>
                      <div className="flex-grow min-w-0">
                        <div className="font-semibold text-sm text-slate-800 dark:text-slate-200 truncate">
                          {user.display_name || user.username}
                        </div>
                        {showBadges && ((user.qualifications && user.qualifications.length > 0) || (user.groups && user.groups.length > 0)) && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {user.qualifications?.map((q, i) => (
                              <span
                                key={`q-${i}`}
                                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/30"
                              >
                                {q}
                              </span>
                            ))}
                            {user.groups?.map((g, i) => (
                              <span
                                key={`g-${i}`}
                                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-900/30"
                              >
                                {g}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* 👥 Create Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <form onSubmit={handleCreateGroup} className="bg-white dark:bg-slate-955 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl animate-in fade-in duration-200">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">👥 Gruppe erstellen</h3>
              <button type="button" onClick={() => { setShowGroupModal(false); setShowGroupFilterSection(false); setGroupQualifications([]); setGroupGroups([]); }} className="text-slate-400 hover:text-slate-600 transition-colors">✕</button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[70vh]">
              {/* Group name */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Gruppenname</label>
                <input
                  type="text"
                  required
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  placeholder="z.B. Logistik-Team"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Admin: Toggle between manual selection and filter */}
              {currentUserProfile?.is_admin && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowGroupFilterSection(false)}
                    className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold border transition-all ${
                      !showGroupFilterSection
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    👤 Manuell auswählen
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowGroupFilterSection(true)}
                    className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold border transition-all ${
                      showGroupFilterSection
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    🎯 Nach Filter
                  </button>
                </div>
              )}

              {/* Manual user selection */}
              {!showGroupFilterSection && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Mitglieder auswählen</label>
                  <div className="max-h-[180px] overflow-y-auto border border-slate-200 dark:border-slate-805 rounded-xl p-2 space-y-2 bg-slate-50 dark:bg-slate-900">
                    {users.map(u => (
                      <label key={u.id} className="flex items-center gap-3 p-2 hover:bg-white dark:hover:bg-slate-800 rounded-lg cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={selectedGroupUsers.includes(u.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedGroupUsers(prev => [...prev, u.id]);
                            } else {
                              setSelectedGroupUsers(prev => prev.filter(id => id !== u.id));
                            }
                          }}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{u.display_name || u.username}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin filter section */}
              {showGroupFilterSection && currentUserProfile?.is_admin && (
                <>
                  {/* Qual filter */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Filter nach Qualifikationen</label>
                    <div className="max-h-[130px] overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl p-2 space-y-1 bg-slate-50 dark:bg-slate-900">
                      {allQualifications.length === 0 ? (
                        <div className="text-xs text-slate-400 p-2">Keine Qualifikationen vorhanden</div>
                      ) : (
                        allQualifications.map(q => (
                          <label key={q.id} className="flex items-center gap-3 p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                            <input
                              type="checkbox"
                              checked={groupQualifications.includes(q.id)}
                              onChange={e => {
                                if (e.target.checked) setGroupQualifications(prev => [...prev, q.id]);
                                else setGroupQualifications(prev => prev.filter(id => id !== q.id));
                              }}
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span>{q.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Group filter */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Filter nach Gruppen</label>
                    <div className="max-h-[130px] overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl p-2 space-y-1 bg-slate-50 dark:bg-slate-900">
                      {allGroups.length === 0 ? (
                        <div className="text-xs text-slate-400 p-2">Keine Gruppen vorhanden</div>
                      ) : (
                        allGroups.map(g => (
                          <label key={g.id} className="flex items-center gap-3 p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                            <input
                              type="checkbox"
                              checked={groupGroups.includes(g.id)}
                              onChange={e => {
                                if (e.target.checked) setGroupGroups(prev => [...prev, g.id]);
                                else setGroupGroups(prev => prev.filter(id => id !== g.id));
                              }}
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span>{g.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Member preview */}
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/30 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase">
                        👥 Vorschau ({previewGroupFilterMembers.length})
                      </span>
                      {(groupQualifications.length === 0 && groupGroups.length === 0) && (
                        <span className="text-[10px] text-slate-400 italic">Wähle Filter um Vorschau zu sehen</span>
                      )}
                    </div>
                    {previewGroupFilterMembers.length === 0 && (groupQualifications.length > 0 || groupGroups.length > 0) ? (
                      <p className="text-xs text-amber-600 dark:text-amber-400">⚠️ Keine Benutzer mit diesen Filtern gefunden</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5 max-h-[70px] overflow-y-auto">
                        {previewGroupFilterMembers.map(u => (
                          <span key={u.id} className="text-[10px] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 px-2 py-0.5 rounded-full font-medium">
                            {u.display_name || u.username}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Auto-sync toggle */}
                  <div className="flex items-start gap-3 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-100 dark:border-blue-900/30">
                    <input
                      type="checkbox"
                      id="groupAutoSyncToggle"
                      checked={groupAutoSync}
                      onChange={e => setGroupAutoSync(e.target.checked)}
                      className="mt-0.5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="groupAutoSyncToggle" className="text-xs text-blue-700 dark:text-blue-300 cursor-pointer">
                      <span className="font-semibold">Auto-Sync aktivieren</span>
                      <p className="text-blue-500 dark:text-blue-400 mt-0.5">Neue Benutzer mit passender Qualifikation/Gruppe werden automatisch hinzugefügt. Benutzer die die Kriterien verlieren werden entfernt.</p>
                    </label>
                  </div>
                </>
              )}
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
              <button type="button" onClick={() => { setShowGroupModal(false); setShowGroupFilterSection(false); setGroupQualifications([]); setGroupGroups([]); }} className="px-4 py-2 bg-slate-200 dark:bg-slate-805 text-slate-800 dark:text-slate-200 rounded-xl text-sm font-semibold">Abbrechen</button>
              <button
                type="submit"
                disabled={showGroupFilterSection && groupQualifications.length === 0 && groupGroups.length === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-sm font-semibold shadow-md shadow-blue-900/25 transition-colors"
              >
                👥 Erstellen
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 📢 Create Broadcast Modal */}
      {showBroadcastModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <form onSubmit={handleCreateBroadcast} className="bg-white dark:bg-slate-955 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl animate-in fade-in duration-200">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">Broadcast-Kanal erstellen</h3>
              <button type="button" onClick={() => setShowBroadcastModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Kanalname</label>
                <input 
                  type="text" 
                  required
                  value={broadcastName}
                  onChange={e => setBroadcastName(e.target.value)}
                  placeholder="z.B. Wichtige Vereinsnews"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Filter nach Qualifikationen</label>
                <div className="max-h-[140px] overflow-y-auto border border-slate-200 dark:border-slate-805 rounded-xl p-2 space-y-2 bg-slate-50 dark:bg-slate-900">
                  {allQualifications.length === 0 ? (
                    <div className="text-xs text-slate-400 p-2">Keine Qualifikationen vorhanden</div>
                  ) : (
                    allQualifications.map(q => (
                      <label key={q.id} className="flex items-center gap-3 p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                        <input 
                          type="checkbox"
                          checked={selectedQualifications.includes(q.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedQualifications(prev => [...prev, q.id]);
                            } else {
                              setSelectedQualifications(prev => prev.filter(id => id !== q.id));
                            }
                          }}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{q.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Filter nach Gruppen</label>
                <div className="max-h-[140px] overflow-y-auto border border-slate-200 dark:border-slate-805 rounded-xl p-2 space-y-2 bg-slate-50 dark:bg-slate-900">
                  {allGroups.length === 0 ? (
                    <div className="text-xs text-slate-400 p-2">Keine Gruppen vorhanden</div>
                  ) : (
                    allGroups.map(g => (
                      <label key={g.id} className="flex items-center gap-3 p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                        <input 
                          type="checkbox"
                          checked={selectedGroups.includes(g.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedGroups(prev => [...prev, g.id]);
                            } else {
                              setSelectedGroups(prev => prev.filter(id => id !== g.id));
                            }
                          }}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{g.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Live Mitglieder-Vorschau */}
              <div className="p-3 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/30 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase">
                    👥 Vorschau Mitglieder ({previewBroadcastMembers.length})
                  </span>
                  {(selectedQualifications.length === 0 && selectedGroups.length === 0) && (
                    <span className="text-[10px] text-slate-400 italic">Wähle Filter um Vorschau zu sehen</span>
                  )}
                </div>
                {previewBroadcastMembers.length === 0 && (selectedQualifications.length > 0 || selectedGroups.length > 0) ? (
                  <p className="text-xs text-amber-600 dark:text-amber-400">⚠️ Keine Benutzer mit diesen Filtern gefunden</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-[80px] overflow-y-auto">
                    {previewBroadcastMembers.map(u => (
                      <span key={u.id} className="text-[10px] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                        {u.photo_url ? (
                          <img src={u.photo_url} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                        ) : (
                          <span className="text-[8px]">👤</span>
                        )}
                        {u.display_name || u.username}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-100 dark:border-blue-900/30">
                <input 
                  type="checkbox"
                  id="autoSyncToggle"
                  checked={autoSyncMembers}
                  onChange={e => setAutoSyncMembers(e.target.checked)}
                  className="mt-1 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="autoSyncToggle" className="text-xs text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                  <span className="font-semibold text-slate-800 dark:text-slate-200 block mb-0.5">Mitglieder automatisch synchronisieren</span>
                  Neue Benutzer automatisch bei passender Qualifikation/Gruppe hinzufügen und bei Verlust entfernen.
                </label>
              </div>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
              <button type="button" onClick={() => setShowBroadcastModal(false)} className="px-4 py-2 bg-slate-200 dark:bg-slate-805 text-slate-800 dark:text-slate-200 rounded-xl text-sm font-semibold">Abbrechen</button>
              <button 
                type="submit" 
                disabled={selectedQualifications.length === 0 && selectedGroups.length === 0}
                className={`px-4 py-2 text-white rounded-xl text-sm font-semibold shadow-md ${
                  (selectedQualifications.length === 0 && selectedGroups.length === 0)
                    ? 'bg-slate-400 cursor-not-allowed opacity-60'
                    : 'bg-blue-600 shadow-blue-900/25 hover:bg-blue-700'
                }`}
              >
                Erstellen
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 🔍 Edit Broadcast Filters Modal */}
      {showFiltersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <form onSubmit={handleUpdateFilters} className="bg-white dark:bg-slate-955 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl animate-in fade-in duration-200">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">Kanal-Filter bearbeiten</h3>
              <button type="button" onClick={() => { setShowFiltersModal(false); setFilterRoomId(null); }} className="text-slate-400 hover:text-slate-600 transition-colors">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Filter nach Qualifikationen</label>
                <div className="max-h-[140px] overflow-y-auto border border-slate-200 dark:border-slate-805 rounded-xl p-2 space-y-2 bg-slate-50 dark:bg-slate-900">
                  {allQualifications.length === 0 ? (
                    <div className="text-xs text-slate-400 p-2">Keine Qualifikationen vorhanden</div>
                  ) : (
                    allQualifications.map(q => (
                      <label key={q.id} className="flex items-center gap-3 p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                        <input 
                          type="checkbox"
                          checked={filterQualifications.includes(q.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setFilterQualifications(prev => [...prev, q.id]);
                            } else {
                              setFilterQualifications(prev => prev.filter(id => id !== q.id));
                            }
                          }}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{q.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Filter nach Gruppen</label>
                <div className="max-h-[140px] overflow-y-auto border border-slate-200 dark:border-slate-805 rounded-xl p-2 space-y-2 bg-slate-50 dark:bg-slate-900">
                  {allGroups.length === 0 ? (
                    <div className="text-xs text-slate-400 p-2">Keine Gruppen vorhanden</div>
                  ) : (
                    allGroups.map(g => (
                      <label key={g.id} className="flex items-center gap-3 p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                        <input 
                          type="checkbox"
                          checked={filterGroups.includes(g.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setFilterGroups(prev => [...prev, g.id]);
                            } else {
                              setFilterGroups(prev => prev.filter(id => id !== g.id));
                            }
                          }}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{g.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Live Mitglieder-Vorschau */}
              <div className="p-3 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/30 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-purple-700 dark:text-purple-400 uppercase">
                    👥 Aktuell betroffene Mitglieder ({previewFilterMembers.length})
                  </span>
                  {(filterQualifications.length === 0 && filterGroups.length === 0) && (
                    <span className="text-[10px] text-slate-400 italic">Wähle Filter um Vorschau zu sehen</span>
                  )}
                </div>
                {previewFilterMembers.length === 0 && (filterQualifications.length > 0 || filterGroups.length > 0) ? (
                  <p className="text-xs text-amber-600 dark:text-amber-400">⚠️ Keine Benutzer mit diesen Filtern gefunden</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-[80px] overflow-y-auto">
                    {previewFilterMembers.map(u => (
                      <span key={u.id} className="text-[10px] bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-800 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                        {u.photo_url ? (
                          <img src={u.photo_url} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                        ) : (
                          <span className="text-[8px]">👤</span>
                        )}
                        {u.display_name || u.username}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3 p-2 bg-purple-50 dark:bg-purple-950/30 rounded-xl border border-purple-100 dark:border-purple-900/30">
                <input 
                  type="checkbox"
                  id="filterAutoSyncToggle"
                  checked={filterAutoSync}
                  onChange={e => setFilterAutoSync(e.target.checked)}
                  className="mt-1 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="filterAutoSyncToggle" className="text-xs text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                  <span className="font-semibold text-slate-800 dark:text-slate-200 block mb-0.5">Mitglieder automatisch synchronisieren</span>
                  Neue Benutzer automatisch bei passender Qualifikation/Gruppe hinzufügen und bei Verlust entfernen.
                </label>
              </div>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
              <button type="button" onClick={() => { setShowFiltersModal(false); setFilterRoomId(null); }} className="px-4 py-2 bg-slate-200 dark:bg-slate-805 text-slate-800 dark:text-slate-200 rounded-xl text-sm font-semibold">Abbrechen</button>
              <button 
                type="submit" 
                disabled={filterQualifications.length === 0 && filterGroups.length === 0}
                className={`px-4 py-2 text-white rounded-xl text-sm font-semibold shadow-md ${
                  (filterQualifications.length === 0 && filterGroups.length === 0)
                    ? 'bg-slate-400 cursor-not-allowed opacity-60'
                    : 'bg-purple-600 shadow-purple-900/25 hover:bg-purple-750'
                }`}
              >
                Speichern
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ⚙️ Release Broadcast Modal */}
      {showReleaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <form onSubmit={handleReleaseBroadcast} className="bg-white dark:bg-slate-955 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl animate-in fade-in duration-200">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">Broadcast freigeben</h3>
              <button type="button" onClick={() => setShowReleaseModal(false)} className="text-slate-400 hover:text-slate-650">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <label className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 cursor-pointer text-sm font-semibold text-slate-800 dark:text-slate-200">
                <input 
                  type="checkbox"
                  checked={releaseIsActive}
                  onChange={e => setReleaseIsActive(e.target.checked)}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Für Schreiber (Admins & freigegebene Personen) freigeben</span>
              </label>
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Freigegebene Schreiber auswählen</label>
                <div className="max-h-[150px] overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-xl p-2 space-y-2 bg-slate-50 dark:bg-slate-900">
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-3 p-2 hover:bg-white dark:hover:bg-slate-850 rounded-lg cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                      <input 
                        type="checkbox"
                        checked={approvedWriters.includes(u.id)}
                        onChange={e => {
                          if (e.target.checked) {
                            setApprovedWriters(prev => [...prev, u.id]);
                          } else {
                            setApprovedWriters(prev => prev.filter(id => id !== u.id));
                          }
                        }}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>{u.display_name || u.username}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
              <button type="button" onClick={() => setShowReleaseModal(false)} className="px-4 py-2 bg-slate-200 dark:bg-slate-805 text-slate-800 dark:text-slate-200 rounded-xl text-sm font-semibold">Abbrechen</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold shadow-md shadow-blue-900/25">Speichern</button>
            </div>
          </form>
        </div>
      )}

      {/* 👤➕ Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <form onSubmit={handleInviteUser} className="bg-white dark:bg-slate-955 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl animate-in fade-in duration-200">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">Mitglied einladen</h3>
              <button type="button" onClick={() => setShowInviteModal(false)} className="text-slate-400 hover:text-slate-650">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Benutzer auswählen</label>
                <select
                  required
                  value={inviteTargetUserId}
                  onChange={e => setInviteTargetUserId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-blue-500"
                >
                  <option value="">-- Mitglied auswählen --</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
              <button type="button" onClick={() => setShowInviteModal(false)} className="px-4 py-2 bg-slate-200 dark:bg-slate-805 text-slate-800 dark:text-slate-200 rounded-xl text-sm font-semibold">Abbrechen</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold shadow-md shadow-blue-900/25">Einladen</button>
            </div>
          </form>
        </div>
      )}

      {/* 📊 Create Poll Modal */}
      {showPollModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <form onSubmit={handleCreatePoll} className="bg-white dark:bg-slate-955 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl animate-in fade-in duration-200">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">📊 Umfrage erstellen</h3>
              <button type="button" onClick={() => setShowPollModal(false)} className="text-slate-400 hover:text-slate-650">✕</button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[70vh]">
              {/* Question */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2">Frage</label>
                <input
                  type="text"
                  required
                  value={pollQuestion}
                  onChange={e => setPollQuestion(e.target.value)}
                  placeholder="z.B. Wer übernimmt die Wochenendschicht?"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Options */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Antwortoptionen</label>
                {pollOptions.map((opt, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="text"
                      required={idx < 2}
                      value={opt}
                      onChange={e => {
                        const newOpts = [...pollOptions];
                        newOpts[idx] = e.target.value;
                        setPollOptions(newOpts);
                      }}
                      placeholder={`Option ${idx + 1}`}
                      className="flex-grow bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl px-4 py-2.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-blue-500"
                    />
                    {pollOptions.length > 2 && (
                      <button
                        type="button"
                        onClick={() => setPollOptions(prev => prev.filter((_, i) => i !== idx))}
                        className="text-red-400 hover:text-red-600 transition-colors text-lg leading-none"
                      >×</button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setPollOptions(prev => [...prev, ''])}
                  className="text-xs text-blue-600 dark:text-blue-400 font-semibold hover:underline"
                >
                  + Option hinzufügen
                </button>
              </div>

              {/* Settings */}
              <div className="space-y-3 pt-1 border-t border-slate-100 dark:border-slate-800">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase pt-1">Einstellungen</p>

                {/* Multi-select toggle */}
                <label className="flex items-center justify-between cursor-pointer group">
                  <div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Mehrfachauswahl erlauben</span>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">Teilnehmer können mehrere Optionen wählen</p>
                  </div>
                  <div
                    className={`relative w-11 h-6 rounded-full transition-all duration-200 ${
                      pollAllowMultiple ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-700'
                    }`}
                    onClick={() => setPollAllowMultiple(prev => !prev)}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200 ${
                      pollAllowMultiple ? 'left-5' : 'left-0.5'
                    }`} />
                  </div>
                </label>

                {/* Expiry date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Ablaufdatum <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-2">Nach diesem Zeitpunkt kann nicht mehr abgestimmt werden</p>
                  <input
                    type="datetime-local"
                    value={pollExpiresAt}
                    onChange={e => setPollExpiresAt(e.target.value)}
                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:border-blue-500"
                  />
                  {pollExpiresAt && (
                    <button
                      type="button"
                      onClick={() => setPollExpiresAt('')}
                      className="text-[10px] text-red-400 hover:text-red-600 mt-1 font-semibold"
                    >
                      Ablaufdatum entfernen ×
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
              <button type="button" onClick={() => setShowPollModal(false)} className="px-4 py-2 bg-slate-200 dark:bg-slate-855 text-slate-800 dark:text-slate-200 rounded-xl text-sm font-semibold">Abbrechen</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold shadow-md shadow-blue-900/25">📊 Senden</button>
            </div>
          </form>
        </div>
      )}

      {/* 🗑️ Löschen-Bestätigungs-Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <form onSubmit={handleDeleteRoom} className="bg-white dark:bg-slate-950 border border-red-300 dark:border-red-900/50 rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl shadow-red-900/20 animate-in fade-in duration-200">
            {/* Modal Header */}
            <div className="p-5 border-b border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 flex items-center gap-3">
              <span className="text-2xl">🗑️</span>
              <div>
                <h3 className="font-bold text-lg text-red-800 dark:text-red-300">Gruppe unwiderruflich löschen</h3>
                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">Diese Aktion kann nicht rückgängig gemacht werden!</p>
              </div>
              <button type="button" onClick={() => setShowDeleteModal(false)} className="ml-auto text-red-400 hover:text-red-600 transition-colors">✕</button>
            </div>

            <div className="p-5 space-y-4">
              {/* Warning box */}
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 rounded-xl text-sm text-amber-800 dark:text-amber-300">
                <p className="font-semibold mb-1">⚠️ Folgendes wird gelöscht:</p>
                <ul className="list-disc list-inside text-xs space-y-0.5 text-amber-700 dark:text-amber-400">
                  <li>Die Gruppe: <strong>„{deleteRoomName}"</strong></li>
                  <li>Alle Nachrichten in dieser Gruppe</li>
                  <li>Alle Mitglieder werden entfernt</li>
                </ul>
              </div>

              {/* Confirmation input */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2">
                  Zur Bestätigung bitte <span className="font-mono font-black text-red-600 dark:text-red-400 tracking-widest">LÖSCHEN</span> eingeben:
                </label>
                <input
                  type="text"
                  autoFocus
                  value={deleteConfirmText}
                  onChange={e => {
                    setDeleteConfirmText(e.target.value);
                    setDeleteError('');
                  }}
                  placeholder="LÖSCHEN"
                  className={`w-full bg-slate-50 dark:bg-slate-900 border rounded-xl px-4 py-3 text-sm font-mono font-bold tracking-widest focus:outline-none transition-all ${
                    deleteConfirmText === 'LÖSCHEN'
                      ? 'border-red-500 text-red-700 dark:text-red-400 focus:border-red-600 focus:ring-1 focus:ring-red-500'
                      : 'border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-200 focus:border-red-400 focus:ring-1 focus:ring-red-400'
                  }`}
                />
                {deleteError && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 font-medium">{deleteError}</p>
                )}
              </div>
            </div>

            <div className="p-4 bg-slate-50 dark:bg-slate-900/40 border-t border-red-200 dark:border-red-900/40 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                  setDeleteError('');
                }}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={deleteConfirmText !== 'LÖSCHEN'}
                className={`px-5 py-2 rounded-xl text-sm font-bold text-white transition-all shadow-md ${
                  deleteConfirmText === 'LÖSCHEN'
                    ? 'bg-red-600 hover:bg-red-700 shadow-red-900/30'
                    : 'bg-slate-400 cursor-not-allowed opacity-60'
                }`}
              >
                🗑️ Endgültig löschen
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
